import React, { useEffect } from 'react';
import styled from 'styled-components';

import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { blue100 } from '@zocdoc-mezzanine/foundations-colors';
import { Link } from '@zocdoc-frontend-common/components-link';
import PaddedContainer from '@zocdoc-frontend-common/components-padded-container';
import { Button } from '@zocdoc-frontend-common/components-button';
import VirtualVisitIcon from '@zocdoc-frontend-common/assets-icons/VirtualVisitIcon';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import VirtualVisitSearchResults from '../VirtualVisitSearchResults';
import { isVaccineProcedure } from '../../../Booking/utils/isCovidVaccine';
import {
    withCustomMetrics,
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc/frontend-metrics';

const NativeLazyLoad = { loading: 'lazy' };

function CoronaErrorView(props) {
    const {
        officePhoneNumber,
        directoryId,
        hasVirtualLocations,
        profileUrl,
        sendMetric,
        professionalName,
        image,
        firstVirtualLocation,
        procedureId,
    } = props;

    useEffect(() => {
        sendMetric({
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.VIEW,
            eventDetails: {
                hasVirtualLocations,
                hasVirtualLocationInSameState:
                    firstVirtualLocation !== undefined,
            },
        });
    }, [sendMetric, hasVirtualLocations, firstVirtualLocation]);

    const renderUnableToBookWithCovidLink = () => {
        return (
            <>
                Based on your responses to the previous questions, and out of an
                abundance of caution with respect to the{' '}
                <Link
                    href="https://www.zocdoc.com/about/coronavirus-covid-19/"
                    target="_blank"
                >
                    coronavirus
                </Link>{' '}
                (COVID-19), we’re unable to complete your booking at this time.
            </>
        );
    };

    const showVirtualSelfLink = hasVirtualLocations && firstVirtualLocation;

    const renderSelfLink = () => {
        if (!showVirtualSelfLink) {
            return null;
        }

        return (
            <>
                <DoctorInfoSection data-test="corona-self-link">
                    <DoctorImageWrapper>
                        <DoctorImage src={image} />
                        <StyledVirtualVisit />
                    </DoctorImageWrapper>
                    <DoctorName>{professionalName}</DoctorName>
                    <ViewVideoVisitAvailability
                        href={`${profileUrl}?LocIdent=${firstVirtualLocation.monolithId}`}
                    >
                        View video visit availability
                    </ViewVideoVisitAvailability>
                </DoctorInfoSection>
                <Paragraph>{renderUnableToBookWithCovidLink()}</Paragraph>
                <Paragraph>
                    {professionalName} offers video visit appointments so you
                    can get the care you need without leaving your home.
                    Schedule your online appointment above or call the practice
                    at{' '}
                    <Link href={`tel:${officePhoneNumber}`}>
                        <Bold>{officePhoneNumber}</Bold>
                    </Link>{' '}
                    with any questions or concerns.
                </Paragraph>
            </>
        );
    };

    const renderInPersonMessaging = () => {
        if (isVaccineProcedure(procedureId)) {
            return <Paragraph>{renderUnableToBookWithCovidLink()}</Paragraph>;
        }
        return (
            <Paragraph>
                {renderUnableToBookWithCovidLink()}
                We advise that you call the practice at{' '}
                <Link href={`tel:${officePhoneNumber}`}>
                    <Bold data-test="in-person-office-phone-number">
                        {officePhoneNumber}
                    </Bold>
                </Link>{' '}
                to discuss your symptoms and healthcare needs prior to seeking
                in-person care.
            </Paragraph>
        );
    };

    const headerText = showVirtualSelfLink
        ? `We're unable to complete your in-person booking at this time, but ${professionalName} does offer video visits.`
        : `We're unable to complete your booking with ${professionalName} at this time.`;
    const vvView =
        !showVirtualSelfLink && directoryId < 0 ? (
            <VirtualVisitSearchResults data-test="virtual-visit-search" />
        ) : null;

    return (
        <>
            <Wrapper data-test="corona-error-view">
                <Header>{headerText}</Header>
                {showVirtualSelfLink
                    ? renderSelfLink()
                    : renderInPersonMessaging()}
            </Wrapper>
            {vvView}
        </>
    );
}

export default withCustomMetrics({
    component: 'CoronaErrorView',
    element: 'CoronaErrorView',
})(CoronaErrorView);

const Wrapper = styled(PaddedContainer)`
    text-align: center;
    max-width: 800px;
    margin: 72px auto;
`;

const Header = styled.h1`
    font: 24px/32px ${FontFamily.SharpSansBold};
    margin: 16px auto;
    max-width: 540px;
`;

const Paragraph = styled.p`
    font: 16px/1.5 ${FontFamily.SharpSansMedium};
    margin: 18px auto;
`;

const Bold = styled.b`
    font-family: ${FontFamily.SharpSansSemibold};
`;

const DoctorInfoSection = styled.div`
    display: flex;
    flex-wrap: wrap;
    padding: 10px 20px 14px;
    background: white;
    align-items: center;
    margin-top: 12px;
`;

const DoctorImageWrapper = styled.div`
    width: 64px;
    height: 64px;
    position: relative;
`;

const DoctorImage = styled.img.attrs(NativeLazyLoad)`
    border-radius: 50%;
    display: block;
    overflow: hidden;
    position: relative;
    width: 100%;
    height: 100%;
`;

const StyledVirtualVisit = styled(VirtualVisitIcon)`
    position: absolute;
    font-size: 24px;
    bottom: 0;
    right: 0;
`;

const DoctorName = styled.div`
    box-sizing: border-box;
    color: ${blue100};
    font: 20px ${FontFamily.SharpSansBold};
    padding-bottom: 3px;
    padding-left: 15px;
    flex: 1;
    text-align: left;
`;

const ViewVideoVisitAvailability = styled(Button)`
    margin-left: auto;
    font-size: 15px;

    margin-top: 20px;
    flex-basis: 100%;
    ${breakpoint.small} {
        margin-top: 0;
        flex-basis: initial;
    }
`;
